import { membershipState } from '../../store/modules/MembershipState'
import axios from 'axios'
import { UserInfoModel } from '../../modules/class/RegisteredUserInfo'
import { DownloadStatus } from '../../modules/class/Downloads'
import GetParameter from './GetParameter'
import UidCookie from '../../modules/util/UidSetCookie'
import PathUtil from '../../modules/util/PathUtil'
import camelcaseKeys from 'camelcase-keys'
import Item from '../../modules/class/Item'
import { createCustomEvent } from '../CustomEvent'
import { callCookieSave } from '../../modules/util/call_cookie_save'
import { callCookieDeleteCheck } from '../../modules/util/call_cookie_delete_check'

// ---------------------------------------------------------------
// 定数
const ACTION_PARAM = 'action'
const SIGNIN_PARAM = 'signin'
const JSON_PATH_CATEGORY = PathUtil.makePath(
  '/json/product_category/product_category_list.json'
)
const JSON_PATH_DOMAIN = PathUtil.makePath('/json/denied_domain.json')
const SAP_DOMAIN = drupalSettings.vueComponent.singleSignOn.domainName
const SAP_PRODUCT_FLG =
  drupalSettings.vueComponent.singleSignOn.interestsProduct
const interestProductSelectorName =
  '.sapcdc-shimadzu-dropdown-profile-product-interest'
const CLOSE_COMMENT = PathUtil.makePath('/json/close.json')
// ---------------------------------------------------------------
// 変数
/** 不足項目確認画面表示用のregToken */
let sapregToken = ''
/** 不足項目確認中のフラグ */
let onPending = false
/** 初回ログインフラグ */
let firstLoginFlg = true
let userEmail = ''
let productCode: string[] = []
let domainDataList: string[] = []
// -----------------------------------------------------------------------------
// INTERFACE
export interface Profile {
  email: string
  locale: string
  lastName: string
  firstName: string
  zip: string
  country: string
  state: string
  city: string
  address: string
}

export interface Data {
  phoneNumber: string
  mobilePhoneNumber: string
  allowContactPhone: boolean
  industry: string
  organization: string
  department: string
  jobRole: string
  isLogicalDeleted: boolean
  logicalDeletedTime: Date
  firstKanaName: string
  lastKanaName: string
}

export interface Subscriptions {
  globalweb: {
    email: {
      isSubscribed: boolean
    }
    sstweb: {
      email: {
        isSubscribed: boolean
      }
    }
  }
}

export interface Preferences {
  terms?: JSON
  privacy?: JSON
}

export interface VerifyLoginResponse {
  regToken: string
  UID: string
  UIDSignature: string
  signatureTimestamp: string
  errorCode: number
  profile: Profile
  data: Data
  subscriptions: Subscriptions
  preferences: Preferences
}

export interface SapLoginInfo {
  UID: string
  UIDSignature: string
  signatureTimestamp: string
}

export interface Site {
  code: string
  label: string
  url: string
  basePath: string
}

/**
 * SAPCDCの「onPendingRegistration」イベントのレスポンス
 * ※不足項目があるときに発火する
 */
export interface SapPendingRegistrationResponse {
  regToken: string
}

/**
 * SAPの不足項目入力画面からのレスポンスデータ
 * 他必要なデータがあれば追加予定
 */
interface SapCustomerConcentScreenContext {
  lang: string
  regToken?: string
}

export interface SapCustomerConcentScreen {
  errorCode: number
  regToken?: string
  context: SapCustomerConcentScreenContext
}

export interface OnAfterSubmitResponse {
  response: SapCustomerConcentScreen
}

export interface SapShowScreenSetResponse {
  response: SapCustomerConcentScreen
}

export interface OnAfterScreenLoadResponse {
  eventName: string
  source: string
  context: object
  currentScreen: string
  form: string
  profile: Profile
  data: JSON
  response: JSON
}

export interface OnBeforeSubmitResponse {
  eventName: string
  source: string
  context: object
  screen: string
  form: string
  profile: Profile
  formData: JSON
  nextScreen: string
}

export interface ErrorModalData {
  errorFlg: boolean
  aipErrorMessage?: Array<string>
  propertiesErrorMessage?: string
}

// -----------------------------------------------------------------------------
// CALLBACK FUNCTION
/**
 * SAP ログインチェック コールバック関数
 * SAPから返却されたUID等をStateに格納する
 * @param response SAPレスポンス
 */
const verifyLoginResponse = async (
  response: VerifyLoginResponse
): Promise<void> => {
  // パラメータの取得
  const uidcookie = new UidCookie()
  const param = GetParameter.currentGetParameter()
  const bookParam = param.get('book')

  callCookieDeleteCheck()

  if (response.errorCode === 0) {
    // 共通処理
    verifyLoginSuccessCommon(response)

    // ユーザーのログイン状態が決定した後に実行される処理 ログイン済み
    createCustomEvent('complete-verify-login', { detail: { isLogin: true } })

    // Cookie保存API呼び出し処理 ログイン直後に1度だけ呼び出す
    window.gigya.accounts.addEventHandlers({
      onLogin: function () {
        callCookieSave(response.UID)
      }
    })

    // 遅延させていたBookmarkの登録
    addWaitBookmark(bookParam)
  } else {
    // ログインしていないときはCookieを消す
    uidcookie.unSetUid()

    if (!onPending) {
      // ユーザーのログイン状態が決定した後に実行される処理 未ログイン
      createCustomEvent('complete-verify-login', { detail: { isLogin: false } })
    }

    // ブックマークのストレージの削除
    if (bookParam === undefined || bookParam === '') {
      localStorage.removeItem('bookmarkStrage')
    }
  }
  // ログイン判定終了
  membershipState.addIsLoadingLogin(false)
}

/**
 * SAP ログインチェック コールバック関数
 * SAPから返却されたUID等をStateに格納する
 * @param response SAPレスポンス
 */
const onPendingRegistrationHandler = async (
  eventData: SapPendingRegistrationResponse
): Promise<void> => {
  const uidcookie = new UidCookie()
  // ログインしていないときはCookieを消す
  uidcookie.unSetUid()
  // パラメータの取得
  const param = GetParameter.currentGetParameter()
  // サービス登録画面では画面内で不足項目確認画面を表示しているため、モーダルでのスクリーンセットの呼び出しは不要。
  // その為、サインインから戻ってきた場合のみモーダルの不足項目確認画面を呼び出す。
  if (param.get(ACTION_PARAM) === SIGNIN_PARAM) {
    sapregToken = eventData.regToken
    onPending = true

    // 拒否ドメインリストの取得
    getDomainData()

    // 不足項目確認画面の表示
    globalwebRegistrationLogin()
  }
}

/**
 * 拒否ドメインリストの取得
 */
const getDomainData = async () => {
  const domainData = await axios.get(JSON_PATH_DOMAIN)
  if (domainData !== undefined) {
    if (domainData.status === 200 && Array.isArray(domainData.data)) {
      domainDataList = domainData.data
    }
  }
}

/**
 * ログイン待ち状態になっていたブックマークの登録を行う
 *
 * @param param
 */
const addWaitBookmark = async (bookParam: string) => {
  let url = ''
  let title = ''
  if (bookParam !== undefined || bookParam !== '') {
    const bookStrage = localStorage.getItem('bookmarkStrage')
    if (bookStrage !== undefined && bookStrage !== '' && bookStrage !== null) {
      const bookData = bookStrage.split('<>')
      if (bookData[0] === bookParam) {
        // ブックマーク登録
        url = bookData[1]
        title = bookData[2]
      }
    }
  }

  if (url !== null && url !== '') {
    createCustomEvent('bookmark-add', {
      urlPath: url,
      title: title,
      callback: (result: boolean) => {
        // ブックマークの追加に成功したら状態を更新
        createCustomEvent('bookmark-update', {
          result: result,
          urlPath: url
        })
      }
    })

    localStorage.removeItem('bookmarkStrage')
  }
}

/**
 * SAP ログインチェック コールバック関数(accessHtml)
 * SAPから返却されたUID等をStateに格納する
 * @param response SAPレスポンス
 */
const verifyLoginResponseAccesshtml = async (
  response: VerifyLoginResponse
): Promise<void> => {
  // パラメータの取得
  const uidcookie = new UidCookie()

  if (response.errorCode === 0) {
    // 共通処理
    verifyLoginSuccessCommon(response)

    // 再ダウンロード用にステータス変更
    if (
      membershipState.pdfDownloadAccessUrl !== '' &&
      membershipState.pdfDownloadStatus === DownloadStatus.NotLoggedIn
    ) {
      membershipState.addDownloadStatus(DownloadStatus.ReDownloadTry)
    }

    // URLクリック処理
    if (membershipState.redirectUrl) {
      // aタグを生成してクリック
      const a = document.createElement('a')
      a.href = membershipState.redirectUrl
      a.click()
    }
  } else {
    // ログインしていないときはCookieを消す
    uidcookie.unSetUid()

    // 不足項目確認中（SSOからリダイレクトで戻ってきた時）は、SSOを行わない。
    // 不足項目確認中でなければSSOを行う。
    if (!onPending) {
      ssoLogin(membershipState.redirectUrl, 'gigya-login-screen')
    }
  }
}

/**
 * verifyLogin成功後の共通処理
 * @param response
 */
const verifyLoginSuccessCommon = async (
  response: VerifyLoginResponse
): Promise<void> => {
  const info: SapLoginInfo = {
    UID: response.UID,
    UIDSignature: response.UIDSignature,
    signatureTimestamp: response.signatureTimestamp
  }

  // Cookieに３点セットをセット
  const uidcookie = new UidCookie()
  uidcookie.setUid(info)

  // ログイン3点セット保持
  membershipState.addSapLoginInfo(info)
  membershipState.addIsLogin(true)
}

/**
 * SAP 会員情報取得 コールバック関数
 * SAPから返却された会員情報をStateに格納する
 * @param response SAPレスポンス
 */
const getUserInfoResponse = async (
  response: VerifyLoginResponse
): Promise<void> => {
  if (response.errorCode === 0) {
    const info: SapLoginInfo = {
      UID: response.UID,
      UIDSignature: response.UIDSignature,
      signatureTimestamp: response.signatureTimestamp
    }

    let isEmailSubscription = false
    if (
      response.subscriptions !== undefined &&
      response.subscriptions.globalweb !== undefined &&
      response.subscriptions.globalweb.email !== undefined
    ) {
      isEmailSubscription = response.subscriptions.globalweb.email.isSubscribed
    }
    const userInfo: UserInfoModel = {
      result: true,
      email: response.profile.email,
      firstname: response.profile.firstName,
      lastname: response.profile.lastName,
      firstKanaName: response.data.firstKanaName,
      lastKanaName: response.data.lastKanaName,
      organization: response.data.organization,
      jobRoles: response.data.jobRole,
      phone: response.data.phoneNumber,
      mobilePhone: response.data.mobilePhoneNumber,
      countryRegion: response.profile.country,
      address: response.profile.address,
      city: response.profile.city,
      state: response.profile.state,
      zipCode: response.profile.zip,
      department: response.data.department,
      industry: response.data.industry,
      emailSubscription: isEmailSubscription,
      locale: response.profile.locale,
      productCategory: [],
      field: [],
      updateDttm: ''
    }

    // ログイン3点セット保持
    membershipState.addSapLoginInfo(info)
    // 会員情報保持
    membershipState.addUserInfo(userInfo)
  } else {
    membershipState.addIsLogin(false)
  }
}

/**
 * 不足項目登録後処理
 * @param response SAPレスポンス
 */
const completeRegistrationSubmit = async (
  response: VerifyLoginResponse
): Promise<void> => {
  if (response.errorCode === 0) {
    if (SAP_PRODUCT_FLG && firstLoginFlg && productCode.length > 0) {
      // ログイン三点セットの取得
      const accountInfo: SapLoginInfo = {
        UID: response.UID,
        UIDSignature: response.UIDSignature,
        signatureTimestamp: response.signatureTimestamp
      }
      // API call
      const form = JSON.stringify({
        interest_product: productCode,
        accountInfo: accountInfo
      })
      const res = await axios.post(
        PathUtil.makePath('/member/api/user_interest_product'),
        form
      )
      if (res === undefined || res.status !== 200) {
        // JavaAPIのステータスコードが200以外の場合
        // エラーメッセージの表示
        const errorData: ErrorModalData = {
          errorFlg: true
        }

        membershipState.addErrorModalData(errorData)
      } else if (res.status === 200 && res.data.result !== 'success') {
        // JavaAPIのステータスが200、レスポンスresultが"error"の場合
        const errorData: ErrorModalData = {
          errorFlg: true,
          aipErrorMessage: res.data.errordata.interest_product
        }

        membershipState.addErrorModalData(errorData)
      }
      // 初期化
      productCode = []
    }
    // 継続処理
    verifyLogin()
  } else {
    // エラーメッセージの表示
    const errorData: ErrorModalData = {
      errorFlg: true,
      propertiesErrorMessage: 'ErrorModal.finalizeRegistration'
    }

    membershipState.addErrorModalData(errorData)
  }
}

// -----------------------------------------------------------------------------
// EXPORT FUNCTION
/**
 * SAPCDC ログインチェック
 * - SAPCDCの verifyLogin を呼び出す
 */
export const verifyLogin = (): void => {
  // ログイン判定開始
  membershipState.addIsLoadingLogin(true)

  window.gigya.accounts.addEventHandlers({
    // 不足項目有の時のイベントハンドラーを追加
    onPendingRegistration: onPendingRegistrationHandler
  })
  window.gigya.accounts.verifyLogin({
    include: 'preferences',
    callback: verifyLoginResponse
  })
}

/**
 * SAPCDC ログインチェック(access.html用)
 * - SAPCDCの verifyLogin を呼び出す
 */
export const verifyLoginAccesshtml = (): void => {
  window.gigya.accounts.verifyLogin({
    include: 'preferences',
    callback: verifyLoginResponseAccesshtml
  })
}

/**
 * SAPCDC 会員情報取得
 * - SAPCDCの verifyLogin を呼び出す
 */
export const getUserInfo = (): void => {
  window.gigya.accounts.getAccountInfo({
    include: 'profile,data,subscriptions,preferences',
    extraProfileFields: 'address',
    callback: getUserInfoResponse
  })
}

/**
 * SAPCDC セントラルログイン
 * - SAPCDCの sso.login を呼び出す
 * - セントラルログインページへの遷移はSAPCDCが制御
 * @param url ログイン/会員登録後リダイレクト先URL
 * @param startscreen セントラルページで表示する画面
 */
export const ssoLogin = (url: string, startscreen: string): void => {
  // signInの印を付ける
  const param = ACTION_PARAM + '=' + SIGNIN_PARAM
  if (url.indexOf(param) < 0) {
    if (url.indexOf('?') < 0) {
      url += '?' + param
    } else {
      url += '&' + param
    }
  }

  window.gigya.sso.login({
    authFlow: 'redirect',
    redirectURL: url,
    lang: drupalSettings.vueComponent.singleSignOn.language,
    context: {
      startScreen: startscreen,
      lang: drupalSettings.vueComponent.singleSignOn.language
    }
  })
}

/**
 *SAP ユーザー情報取得処理結果
 *@param res SAPレスポンス
 */
export const accountInfoResponse = (res: VerifyLoginResponse) => {
  userEmail = res.profile.email
  if (res.preferences.terms !== undefined) {
    for (const sapTermsName of Object.keys(res.preferences.terms)) {
      if (sapTermsName === SAP_DOMAIN) {
        // 一致する場合は初回ログインではない
        firstLoginFlg = false
        break
      }
    }
  }
}

/**
 * 不足項目スクリーンセット呼び出し
 * - SAPCDCの 不足項目スクリーンセットを呼び出す
 * - セントラルログインページへの遷移はSAPCDCが制御
 * @param url リダイレクト先URL
 * @param regToken トークン
 */
export const globalwebRegistrationLogin = (): void => {
  window.gigya.accounts.getAccountInfo({
    include: 'profile,data,subscriptions,preferences',
    extraProfileFields: 'address',
    regToken: sapregToken,
    callback: accountInfoResponse
  })
  window.gigya.accounts.showScreenSet({
    screenSet: drupalSettings.vueComponent.singleSignOn.screenSet.registration,
    startScreen: 'gigya-complete-registration-screen',
    regToken: sapregToken,
    lang: drupalSettings.vueComponent.singleSignOn.language,
    context: {
      lang: drupalSettings.vueComponent.singleSignOn.language
    },
    onAfterSubmit: onAfterSubmitComplete,
    onAfterScreenLoad: onAfterScreenLoadComplete,
    onBeforeSubmit: onBeforeSubmitComplete
  })
}

/**
 * 不足項目入力画面Sumbit後の処理
 * @param eventData 不足項目登録処理結果
 */
export const onAfterSubmitComplete = async (
  eventData: OnAfterSubmitResponse
) => {
  if (eventData.response.errorCode === 0) {
    // 不足項目入力画面のSubmitが成功したことを確認
    window.gigya.accounts.finalizeRegistration({
      regToken: sapregToken,
      callback: completeRegistrationSubmit
    })
  }
}

/**
 * 不足項目入力画面に興味のある製品を追加
 * @param eventData 不足項目登録画面表示処理結果
 */
export const setInterestsProduct = async (
  eventData: OnAfterScreenLoadResponse
) => {
  let productCategoryList: Item[] = []

  const interestsProductElement = document.querySelectorAll<HTMLElement>(
    '#' +
      eventData.currentScreen +
      ' ' +
      interestProductSelectorName +
      ' select'
  )
  if (interestsProductElement.length > 0) {
    // プロダクトカテゴリ取得
    const res = await axios.get(JSON_PATH_CATEGORY)
    if (res === undefined || res.status !== 200) {
      return
    }
    const result = camelcaseKeys(res.data, { deep: true })
    productCategoryList = result.map(
      (d: { categoryId: number; name: string }) =>
        new Item(String(d.categoryId), d.name)
    )
    // プロダクトカテゴリを選択肢に追加
    for (const selectOjt of interestsProductElement) {
      // 初期値の追加
      const option = document.createElement('option')
      option.value = ''
      option.textContent = ''
      selectOjt.appendChild(option)
      for (const productCategory of productCategoryList) {
        if (selectOjt !== undefined && selectOjt !== null) {
          // 選択肢の追加
          const option = document.createElement('option')
          option.value = productCategory.id
          option.textContent = productCategory.title
          selectOjt.appendChild(option)
        }
      }
    }
  }
}

/**
 * 不足項目確認画面の保存直線処理
 * @param eventData 画面情報
 */
export const onBeforeSubmitComplete = async (
  eventData: OnBeforeSubmitResponse
) => {
  // SAP登録処理直前
  if (SAP_PRODUCT_FLG && firstLoginFlg) {
    const selectOjtAll = document.querySelectorAll<HTMLSelectElement>(
      '#' + eventData.screen + ' ' + interestProductSelectorName + ' select'
    )

    // 興味のある製品のカテゴリIDを取得
    if (selectOjtAll.length > 0) {
      for (const selectOjt of selectOjtAll) {
        // 興味のある製品が選択されていたら、変数にセットしておく
        if (selectOjt.value !== '') {
          productCode.push(selectOjt.value)
        }
      }
    }
  }
}

/**
 * 不足項目確認画面表示直後の処理
 * @param eventData 画面情報
 */
export const onAfterScreenLoadComplete = async (
  eventData: OnAfterScreenLoadResponse
): Promise<void> => {
  onPending = false

  // スクリーンセット表示完了時
  const currentScreenId = '#' + eventData.currentScreen + ' '
  // メールドメインチェック機能
  if (domainDataList.length > 0) {
    checkMailDomain(currentScreenId)
  }
  // 興味のある製品表示
  const interestProductElement = document.querySelectorAll<HTMLElement>(
    currentScreenId + interestProductSelectorName
  )

  if (interestProductElement.length > 0) {
    if (!firstLoginFlg) {
      // 初回ログインではない為、興味のある製品を非表示
      for (const element of interestProductElement) {
        element.style.display = 'none'
      }
    } else {
      if (SAP_PRODUCT_FLG) {
        // 興味のある製品の製品カテゴリを取得
        setInterestsProduct(eventData)
      } else {
        for (const element of interestProductElement) {
          element.style.display = 'none'
        }
      }
    }
  }
  // 国選択肢設定
  setCountry(eventData)

  // 都道府県の非表示化
  toggleStateDisplay(currentScreenId)

  // ダブルオプトイン案内文言の非表示化
  toggleOptinMessageDisplay(currentScreenId)

  // CSS崩れ修正
  completeRegisterCss(currentScreenId)

  // 閉じるボタンの対応
  getCloseMessage()
}

/**
 * 不足項目画面の閉じるボタンのメッセージ取得
 */
const getCloseMessage = async () => {
  // メッセージの取得
  const closeCommentData = await axios.get(CLOSE_COMMENT)
  if (closeCommentData !== undefined) {
    if (
      closeCommentData.status === 200 &&
      !(
        closeCommentData.data.comment === '' ||
        closeCommentData.data.comment === undefined
      )
    ) {
      remakeCloseButton(closeCommentData.data.comment)
    }
  }
}

/**
 * 不足項目画面の閉じるボタンの作成
 * @param closeComment メッセージ
 */
const remakeCloseButton = (closeComment: string) => {
  // 閉じるボタンの取得
  const closeButton = document.querySelector<HTMLElement>(
    'div.gigya-screen-dialog-close'
  )
  if (!closeButton) {
    return
  }

  // 不足項目モーダルのヘッダーを取得
  const dialogTop = document.querySelector<HTMLElement>(
    'div.gigya-screen-dialog-top'
  )
  if (!dialogTop) {
    return
  }

  // 閉じるボタンをコピーしてから再作成したボタンを配置
  const cCloseButton = closeButton.cloneNode(true)
  closeButton.remove()
  dialogTop.appendChild(cCloseButton)

  // 再作成した閉じるボタンの取得
  const newCloseButton = document.querySelector<HTMLElement>(
    'div.gigya-screen-dialog-close'
  )
  if (newCloseButton) {
    // 閉じるボタン押下時のイベント追加
    newCloseButton.addEventListener('click', function () {
      if (confirm(closeComment)) {
        // OKの場合は不足項目確認モーダルごと閉じる
        const dialog = document.querySelector<HTMLElement>(
          'div.gigya-screen-dialog'
        )
        if (dialog) {
          dialog.remove()
        }
      }
    })
  }
}

/**
 * 都道府県の非表示化処理
 * 分析日本語サイトの不足項目画面のスクリーンセットの初回表示時のみ、都道府県の非表示化を行う
 * 注）入力時の切替はSAPのUIbuilderの設定で行っている
 * @param currentScreenId 画面名
 */
const toggleStateDisplay = (currentScreenId: string) => {
  const stateName = '.toggle-state-display'
  /** 都道府県の非表示フラグ */
  let hiddenFlg = false
  /** ユーザーの国（ドロップダウン） */
  const userCountryElement = document.querySelector<HTMLSelectElement>(
    currentScreenId +
      '.sapcdc-' +
      SAP_DOMAIN +
      '-dropdown-profile-country select'
  )
  /** ユーザーの都道府県（テキストフィールド） */
  const userStateElement = document.querySelector<HTMLSelectElement>(
    currentScreenId + stateName + ' input'
  )
  /** ユーザーの都道府県（ドロップダウン） */
  const selectElement = document.querySelector<HTMLSelectElement>(
    currentScreenId + stateName + ' select'
  )

  if (userCountryElement && userStateElement && selectElement) {
    /** ユーザーの国 */
    const userCountry = userCountryElement.value

    // 国が日本でドロップダウンに正しい都道府県が登録されている場合は、都道府県を非表示にする
    if (userCountry === 'JAPAN' && selectElement.value !== '') {
      hiddenFlg = true
    }

    // 国が日本以外で都道府県が登録されている場合は、都道府県を非表示にする
    if (userCountry !== 'JAPAN' && userStateElement.value !== '') {
      hiddenFlg = true
    }

    if (hiddenFlg) {
      const stateElement = document.querySelectorAll<HTMLElement>(
        currentScreenId + stateName
      )
      // 都道府県のドロップダウンとテキストフィールドを非表示にする
      for (const element of stateElement) {
        element.style.display = 'none'
      }
    }
  }
}

/**
 * ダブルオプトイン案内文言非表示化
 * メール配信希望チェックを持つスクリーンセットには、ダブルオプトインメール確認を促す案内文言を表示する
 * メール配信希望チェックがスクリーンセットに表示されていない場合は、ダブルオプトインメール確認を促す案内文言を非表示にする
 * 注）入力時の切替はSAPのUIbuilderの設定で行っている
 * @param currentScreenId 画面名
 */
const toggleOptinMessageDisplay = (currentScreenId: string) => {
  /** メール配信希望チェックボックスエレメント */
  const subscriptionElement = document.querySelector<HTMLSelectElement>(
    currentScreenId + '.js-subscriptions-checkbox .subscription-checkbox'
  )
  /** ダブルオプトインメール確認を促す案内文言エレメント */
  const optinMessageElement = document.querySelector<HTMLSelectElement>(
    currentScreenId + '.sapcdc-label-optin-info'
  )

  // メール配信希望チェックボックスがあるサイトのみ以下を実施
  if (subscriptionElement && optinMessageElement) {
    /** メール配信チェックボックスが非表示かどうかプラグ */
    const invalidFlag = subscriptionElement.dataset?.originalValue === 'true'

    if (invalidFlag) {
      // メール配信希望チェックボックスが表示されていない時、案内文言を非表示にする
      optinMessageElement.style.display = 'none'
    }
  }
}

/**
 * メールドメインチェック機能
 * @param currentScreenId スクリーンセット名
 * @returns なし
 */
export const checkMailDomain = async (currentScreenId: string) => {
  const userMainDomain = userEmail.substring(userEmail.indexOf('@'))
  for (const domain of domainDataList) {
    if (userMainDomain.indexOf(domain) !== -1) {
      // エラーメッセージの表示
      const domainMsg = document.querySelector<HTMLElement>(
        currentScreenId + '.sapcdc-' + SAP_DOMAIN + '-domain-check'
      )
      if (domainMsg) {
        domainMsg.style.display = 'block'
      }
      // ボタンの削除
      const saveButton = document.querySelector<HTMLElement>(
        currentScreenId + '.sapcdc-' + SAP_DOMAIN + '-button-submit'
      )
      if (saveButton) {
        saveButton.remove()
      }
      // GAへ送信
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        'denied-domain': domain,
        event: 'ServiceRegistration'
      })
      break
    }
  }
}

/**
 * 不足項目確認画面のCSS崩れ修正処理
 * @param currentScreenId 画面名
 */
export const completeRegisterCss = (currentScreenId: string) => {
  const subscriptionCheckbox = document.querySelector(
    currentScreenId +
      '.sapcdc-' +
      SAP_DOMAIN +
      '-checkbox-subscriptions-' +
      SAP_DOMAIN
  )
  if (subscriptionCheckbox) {
    subscriptionCheckbox?.addEventListener('change', () => {
      // 一番下層のスクリーン
      const parentScreen = document.querySelector<HTMLElement>(
        '#gigya-screen-dialog-page-overlay'
      )
      // ダイアログの下のスクリーン
      const childScreen = document.querySelector<HTMLElement>(
        '.gigya-screen-dialog-main'
      )
      // ダイアログ
      const dialogElement = document.querySelector<HTMLElement>(
        '.gigya-screen-dialog'
      )
      if (parentScreen) {
        const parentHeight = parentScreen.clientHeight
        if (childScreen) {
          const childHeight = childScreen.offsetHeight
          if (dialogElement) {
            const dialogHeight = (parentHeight - childHeight) / 2
            dialogElement.style.top = dialogHeight + 'px'
          }
        }
      }
    })
  }
}

/**
 * ログイン済みかを判定し、処理を実行する
 *
 * @param success
 *   ログイン済みの場合の処理
 * @param failed
 *   未ログインの場合の処理
 * @param failed
 *   ログインの有無にかかわらず実行する処理
 */
export const loginCheck = (success: () => void, failed?: () => void) => {
  const uidcookie = new UidCookie()

  // 既にCookieを保有している場合は、成功判定
  if (uidcookie.hasCookie()) {
    success()
    return
  }

  window.gigya.accounts.verifyLogin({
    include: 'preferences',
    callback: (response: VerifyLoginResponse) => {
      if (response.errorCode === 0) {
        // ログインに成功している場合、Cookieに３点セットをセット
        uidcookie.setUid(response)
        success()
      } else {
        // ログインに失敗している場合、Cookieを削除
        uidcookie.unSetUid()
        if (failed) {
          failed()
        }
      }
    }
  })
}

/**
 * 国の選択肢を設定する
 * @param event 不足項目画面表示完了イベント
 */
export const setCountry = async (event: OnAfterScreenLoadResponse) => {
  if (event.currentScreen === 'gigya-complete-registration-screen') {
    const userCountry = event.profile.country
    const currentScreenId = '#' + event.currentScreen + ' '
    const showScreenSetLang = drupalSettings.vueComponent.singleSignOn.language

    const country = document.querySelector<HTMLSelectElement>(
      currentScreenId +
        '.sapcdc-' +
        SAP_DOMAIN +
        '-dropdown-profile-country select'
    )

    if (country) {
      const data: Record<string, string> = await fetchCountryList(
        showScreenSetLang
      )

      for (const key in data) {
        const option = new Option(data[key], key)
        country.appendChild(option)
        // 登録済みのcountryがあれば選択状態にし、selectボックスを非表示にする
        if (userCountry === key) {
          option.selected = true
          const countryParent = country.parentElement
          if (countryParent) {
            countryParent.style.display = 'none'
          }
        }
      }
    }
  }
}

/**
 * 国の選択肢を取得
 * @param showScreenSetLang 不足項目画面の表示言語
 */
export const fetchCountryList = async <T>(
  showScreenSetLang: string
): Promise<T> => {
  // 不足項目画面の表示言語によってjsonファイルを切り替える
  let jsonPath = `json/country/en.json`
  if (showScreenSetLang === 'ja') {
    jsonPath = `json/country/ja.json`
  } else if (showScreenSetLang === 'zh-tw') {
    jsonPath = `json/country/zhTw.json`
  }
  const path = drupalSettings.vueComponent.idShimadzu.domain + jsonPath
  try {
    const res = await axios.get(path)
    const data = res.data as T
    return data
  } catch (error) {
    return {} as T
  }
}

class ExecuteApiError extends Error {
  constructor(public statusCode: number, e?: string) {
    super(e)
  }
}

/**
 * 認証後にAPIのチェックを行う
 *
 * @param apiPath APIのパス
 * @param form APIに送信する内容
 * @returns APIから受け取った内容
 */
export const executeApiAfterLoginCheck = async <T>(
  apiPath: string,
  form: Record<string, unknown>
): Promise<T> => {
  const uidcookie = new UidCookie()

  const promise = new Promise<T>((resolve, reject) => {
    // ログインチェックを行う
    window.gigya.accounts.verifyLogin({
      include: 'preferences',
      callback: async (response: VerifyLoginResponse) => {
        if (response.errorCode === 0) {
          // ログインに成功している場合、Cookieに３点セットをセット
          uidcookie.setUid(response)

          // APIを呼び出す
          const res = await axios.post(apiPath, form)
          if (res.status === 200) {
            // APIの実行に成功
            resolve(res.data)
          } else {
            // APIの実行に失敗
            reject(
              new ExecuteApiError(
                res.status,
                'api execute error :' + res.status.toString()
              )
            )
          }
        } else {
          // ログインに失敗
          // Cookieを削除
          uidcookie.unSetUid()
        }
      }
    })
  })

  return await promise
}
